import React, { useState, useEffect } from 'react';

const SecurityCaptcha = ({ onVerify, onError, onCancel }) => {
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);

  // Generar CAPTCHA simple
  const generateCaptcha = () => {
    // Sin caracteres ambiguos (0/O/o, 1/l/I) y sin letras que solo difieren en mayúscula/minúscula
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Crear imagen CAPTCHA usando canvas
  const createCaptchaImage = (text) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    canvas.width = 150;
    canvas.height = 50;
    
    // Fondo degradado
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#f0f0f0');
    gradient.addColorStop(1, '#e0e0e0');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Líneas de ruido más sutiles (reducir de 5 a 2)
    for (let i = 0; i < 2; i++) {
      ctx.strokeStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.2)`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }
    
    // Texto con variaciones más leíbles
    ctx.font = 'bold 24px Arial';
    ctx.fillStyle = '#1f2937';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Espaciado más consistente para mejor legibilidad
    const charWidth = canvas.width / text.length;
    
    for (let i = 0; i < text.length; i++) {
      const x = charWidth * i + charWidth / 2;
      const y = canvas.height / 2 + (Math.random() - 0.5) * 5; // Reducir variación vertical
      const angle = (Math.random() - 0.5) * 0.15; // Reducir rotación de 0.5 a 0.15 (más legible)
      
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.fillText(text[i], 0, 0);
      ctx.restore();
    }
    
    return canvas.toDataURL();
  };

  const [currentCaptcha, setCurrentCaptcha] = useState('');

  useEffect(() => {
    const newCaptcha = generateCaptcha();
    setCurrentCaptcha(newCaptcha);
    setCaptchaImage(createCaptchaImage(newCaptcha));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (captchaCode.replace(/\s+/g, '').toLowerCase() === currentCaptcha.toLowerCase()) {
        setAttempts(0);
        onVerify();
      } else {
        setAttempts(prev => prev + 1);
        onError('Código CAPTCHA incorrecto. Intenta de nuevo.');
        
        // Generar nuevo CAPTCHA después de error
        const newCaptcha = generateCaptcha();
        setCurrentCaptcha(newCaptcha);
        setCaptchaImage(createCaptchaImage(newCaptcha));
        setCaptchaCode('');
      }
    } catch (error) {
      onError('Error verificando CAPTCHA. Intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshCaptcha = () => {
    const newCaptcha = generateCaptcha();
    setCurrentCaptcha(newCaptcha);
    setCaptchaImage(createCaptchaImage(newCaptcha));
    setCaptchaCode('');
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200 max-w-md mx-auto">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Verificación de Seguridad
        </h3>
        <p className="text-sm text-gray-600">
          Por seguridad, completa el CAPTCHA para continuar
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="text-center">
          <div className="inline-block p-2 bg-gray-100 rounded border">
            <img 
              src={captchaImage} 
              alt="CAPTCHA" 
              className="block"
              style={{ imageRendering: 'pixelated' }}
            />
          </div>
          <button
            type="button"
            onClick={refreshCaptcha}
            className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
          >
            🔄 Generar nuevo código
          </button>
        </div>

        <div>
          <label htmlFor="captcha" className="block text-sm font-medium text-gray-700 mb-1">
            Ingresa el código que ves arriba:
          </label>
          <input
            type="text"
            id="captcha"
            value={captchaCode}
            onChange={(e) => setCaptchaCode(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-lg tracking-widest"
            placeholder="Código CAPTCHA"
            maxLength="8"
            required
            autoComplete="off"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
          />
        </div>

        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onCancel || refreshCaptcha}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isLoading || !captchaCode.trim()}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Verificando...' : 'Verificar'}
          </button>
        </div>

        {attempts > 0 && (
          <div className="text-center">
            <p className="text-sm text-red-600">
              Intentos fallidos: {attempts}/3
            </p>
          </div>
        )}
      </form>
    </div>
  );
};

export default SecurityCaptcha;
